import type { DraftAction } from '../../models/aiAnalysis/workflow'

// The section and the composer are separate React roots, and any draft event
// re-renders the section from server truth — component state there does not
// survive. The held draft-context lives here so both roots read one answer.
type Listener = () => void

let held: DraftAction | null = null
let pendingInsert = false
let decidedIds: Set<string> = new Set()
let listeners: Listener[] = []

const notify = () => listeners.forEach((listener) => listener())

export const handoffStore = {
  getHeld: (): DraftAction | null => held,

  // Decided ids stay retired for the page's life: the server payload lags the
  // decision by one event, and a re-rendered section must not resurrect a card
  // the scheduler already decided from the composer.
  isDecided: (actionId: string): boolean => decidedIds.has(actionId),

  take: (action: DraftAction): void => {
    held = action
    pendingInsert = true
    notify()
  },

  release: (): void => {
    held = null
    notify()
  },

  // A take arms exactly one insert; whichever composer instance consumes it
  // fills its box, and any later mount into the same hold must not.
  consumePendingInsert: (): boolean => {
    const armed = pendingInsert
    pendingInsert = false
    return armed
  },

  // The id pins retirement to the decision that resolved, not to whatever is
  // held by then; the hold survives when it has moved to another action.
  markDecided: (actionId: string): void => {
    decidedIds.add(actionId)
    if (held && held.id === actionId) held = null
    notify()
  },

  subscribe: (listener: Listener): (() => void) => {
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((existing) => existing !== listener)
    }
  },

  _reset: (): void => {
    held = null
    pendingInsert = false
    decidedIds = new Set()
    listeners = []
  }
}
